/* eslint-disable */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import s from './panels.module.css';
import { Ico } from '../ShellIcons';
import { useNotificationHistory } from '../../../hooks/useNotificationHistory';
import { BriefingEntry } from '../../system/briefing/BriefingEntry';
import { useNotificationsStore } from '../../../stores/notificationsStore';
import { useAttachmentEntities } from '../../../hooks/useAttachmentEntities';
import { readAttachments, legacyAttachment } from '../../../utils/attachmentRegistry';
import { canOpenNotification, openNotification } from '../../../utils/entityRoutes';
import { supabase } from '../../../supabase';

// ============================================================
// NotificationsPanel — the prototype's 440px panel under the bell (S4): class
// tabs with counts, Today / Yesterday / Earlier groups, a row with the unread
// dot · 44px thumbnail (the first attachment's photo, the sender's initials
// or a class tile) · title · 2-line body · class chip · meta, a photo strip
// when the event carries photos, Open on hover. Same reads and writes as the
// legacy drawer (useNotificationHistory: markSeen · markRead · markAllRead);
// "Open inbox" opens that drawer for archive / undo / the older pages.
// ============================================================

const CLASSES = [['all', 'All'], ['men', 'Mentions'], ['qc', 'QC'], ['ord', 'Orders'], ['dev', 'Developments'], ['sys', 'System']];
export function classOf(n) {
  const k = String(n?.type_key || '');
  if (/mention|comment/.test(k) || n?.notification_types?.source === 'personal') return 'men';
  if (k.startsWith('qc.')) return 'qc';
  if (k.startsWith('orders.')) return 'ord';
  if (k.startsWith('dev') || k.startsWith('developments.')) return 'dev';
  return 'sys';
}
const CLASS_ICON = { men: 'comment', qc: 'shield', ord: 'box', dev: 'pencil', sys: 'megaphone' };
function bucket(iso) {
  const d = new Date(iso); const t = new Date();
  const day = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((day(t) - day(d)) / 86400000);
  return diff <= 0 ? 'Today' : diff === 1 ? 'Yesterday' : 'Earlier';
}
function when(iso) {
  const d = new Date(iso); const diff = Date.now() - d.getTime();
  if (diff < 3600e3) return `${Math.max(1, Math.round(diff / 60e3))} min`;
  if (diff < 86400e3) return `${Math.round(diff / 3600e3)} h`;
  if (diff < 7 * 86400e3) return d.toLocaleDateString('en-GB', { weekday: 'short' }) + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' });
}
const initials = (name) => String(name || '').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

function Row({ r, sender, onOpen }) {
  const n = r.notifications; const cls = classOf(n);
  const refs = useMemo(() => { const p = readAttachments(n.payload); if (p.length) return p; const l = legacyAttachment(n.entity_type, n.entity_id); return l ? [l] : []; }, [n]);
  const { byKey } = useAttachmentEntities(refs);
  const ents = refs.map((x) => byKey[x.kind + '|' + x.id + (x.byEntityId ? '|e' : '')]).filter((e) => e && e.image);
  const photo = ents[0] ? ents[0].image : null;
  const mini = n.severity === 'critical' ? 'warn' : n.severity === 'important' ? 'amber' : /approved|completed|landed/i.test(n.title || '') ? 'ok' : null;
  const canGo = canOpenNotification(n);
  return (
    <button type="button" className={`${s.nItem} ${r.read_at ? s.read : s.unread}`} onClick={() => onOpen(r)} data-test={`notif-row-${r.id}`}>
      <span className={s.ud} />
      {photo ? <span className={s.thumb}><img src={photo} alt="" loading="lazy" />{mini ? <span className={`${s.mini} ${s[mini]}`}><Ico name={mini === 'ok' ? 'check' : mini === 'warn' ? 'x' : 'arrow'} /></span> : null}</span>
        : cls === 'men' && sender ? <span className={`${s.thumb} ${s.av}`}>{initials(sender)}</span>
        : <span className={`${s.thumb} ${s.sys} ${cls === 'qc' ? s.qc : cls === 'ord' ? s.ord : ''}`}><Ico name={CLASS_ICON[cls]} />{mini ? <span className={`${s.mini} ${s[mini]}`}><Ico name={mini === 'ok' ? 'check' : mini === 'warn' ? 'x' : 'arrow'} /></span> : null}</span>}
      <span className={s.nt}>
        <span className={s.tt}>{n.title}</span>
        {n.body ? <span className={s.tb}>{n.body}</span> : null}
        {ents.length > 1 ? <span className={s.nPics}>{ents.slice(0, 3).map((e, i) => <span key={i}><img src={e.image} alt="" loading="lazy" /></span>)}{ents.length > 3 ? <span className={s.more}>+{ents.length - 3}</span> : null}</span> : null}
        <span className={s.tm}><span className={`${s.cls} ${s[cls]}`}>{CLASSES.find((c) => c[0] === cls)[1]}</span>{sender ? <span>{sender} · </span> : null}<span>{when(n.created_at || r.created_at)}</span></span>
      </span>
      <span className={s.go} aria-hidden="true" title={canGo ? 'Open' : 'Mark read'}><Ico name={canGo ? 'arrow' : 'check'} /></span>
    </button>
  );
}

export default function NotificationsPanel({ open, onClose, onGoProfile }) {
  const h = useNotificationHistory({ enabled: open });
  const setShowDrawer = useNotificationsStore((st) => st.setShowDrawer);
  const [tab, setTab] = useState('all');
  const [senders, setSenders] = useState({});
  const seen = useRef(false);
  useEffect(() => { if (!open) { seen.current = false; return; } if (seen.current || h.isLoading || !h.rows.length) return; seen.current = true; h.markSeen(h.rows.filter((r) => !r.seen_at).map((r) => r.id)); }, [open, h.isLoading, h.rows]);
  useEffect(() => {
    const ids = [...new Set(h.rows.map((r) => r.notifications?.created_by).filter(Boolean))].filter((id) => !(id in senders));
    if (!ids.length) return;
    let live = true;
    supabase.from('user_profiles_public').select('id, display_name, email').in('id', ids.slice(0, 50)).then(({ data }) => { if (!live) return; const m = {}; (data || []).forEach((u) => { m[u.id] = u.display_name || (u.email ? u.email.split('@')[0] : ''); }); ids.forEach((id) => { if (!(id in m)) m[id] = ''; }); setSenders((p) => ({ ...p, ...m })); });
    return () => { live = false; };
  }, [h.rows]);

  const rows = h.rows.filter((r) => r.notifications);
  const counts = useMemo(() => { const c = { all: 0, men: 0, qc: 0, ord: 0, dev: 0, sys: 0 }; rows.forEach((r) => { if (r.read_at) return; c.all++; c[classOf(r.notifications)]++; }); return c; }, [rows]);
  const list = rows.filter((r) => tab === 'all' || classOf(r.notifications) === tab);
  const unread = counts.all;
  const onOpen = (r) => { if (!r.read_at) h.markRead(r.id); const went = openNotification(r.notifications); if (went) onClose(); };

  let g = '';
  return (
    <div className={`${s.panel} ${open ? s.open : ''}`} role="dialog" aria-label="Notifications" aria-hidden={!open} data-shell-pop data-test="notifications-panel" onClick={(e) => e.stopPropagation()}>
      <div className={s.pHead}>
        <h3>Notifications <small>{unread ? `${unread} unread` : 'all read'}</small></h3><span className={s.sp} />
        <button type="button" className={s.pLink} disabled={!unread} onClick={() => h.markAllRead()}><Ico name="check" />Mark all read</button>
        <button type="button" className={s.pIcon} title="Notification settings" aria-label="Notification settings" onClick={() => { onClose(); onGoProfile && onGoProfile(); }}><Ico name="gear" /></button>
      </div>
      <BriefingEntry onBefore={onClose} />
      <div className={s.pTabs}>{CLASSES.map(([k, l]) => <button key={k} type="button" className={`${s.pTab} ${tab === k ? s.on : ''}`} onClick={() => setTab(k)}>{l}<span className={s.k}>{counts[k]}</span></button>)}</div>
      <div className={s.pList}>
        {open && (h.isLoading && !rows.length ? <div className={s.pEmpty}>Reading…</div>
          : h.isError ? <div className={s.pEmpty}><b>Could not read the notifications</b><button type="button" className={s.pLink} onClick={h.retry}>Try again</button></div>
          : !list.length ? <div className={s.pEmpty}><b>Nothing here</b>{tab === 'all' ? 'No notifications yet' : 'No notifications in this class'}</div>
          : list.map((r) => { const b = bucket(r.notifications.created_at || r.created_at); const head = b !== g ? <div key={'g' + b} className={s.pGrp}><span>{b}</span><span>{b === 'Today' && counts.all ? `${list.filter((x) => !x.read_at && bucket(x.notifications.created_at || x.created_at) === 'Today').length} new` : ''}</span></div> : null; g = b; return <React.Fragment key={r.id}>{head}<Row r={r} sender={senders[r.notifications.created_by]} onOpen={onOpen} /></React.Fragment>; }))}
        {open && h.hasMore ? <div className={s.pEmpty}><button type="button" className={s.pLink} disabled={h.loadingMore} onClick={h.loadOlder}>{h.loadingMore ? 'Loading…' : 'Older'}</button></div> : null}
      </div>
      <div className={s.pFoot}><span>{rows.length} recent · {rows.filter((r) => r.read_at).length} read</span><button type="button" className={s.pLink} onClick={() => { onClose(); setShowDrawer(true); }}>Open inbox <Ico name="arrow" /></button></div>
    </div>
  );
}
